/**
 * Settings → Import → Google Drive (owners and admins; docs/features/google-drive-import.md, rules in logic/googleDrive):
 * every chosen person's My Drive and every chosen shared drive into Files, through the workspace's Google Workspace
 * connection (Settings → Mail → Import mail). Stages (`driveImportStage`): connect → (Drive scope missing: the Admin
 * console steps, "Check again") → read → review (people and shared drives, what fits) → importing (per person / drive)
 * → ended (what came over, what didn't, "Import again", Move to trash / Delete permanently). Polls the job every POLL_MS
 * while it reads, imports or an undo runs. Online only.
 */
import type { GoogleDriveImportOptions, ImportJob, ImportTrashCounts } from "@bundle/contracts";
import { msg, type MessageDescriptor, type MessageKey } from "../i18n.ts";
import { type DriveImportStage, driveChoiceFrom, driveImportStage, driveProblemsCsv, driveReviewSummary, type DriveReviewSummary, GOOGLE_DRIVE_ALL_SCOPES } from "../logic/googleDrive.ts";
import { isRunning } from "../logic/imports.ts";
import { isManager } from "../logic/roles.ts";
import { ApiFailure, errorKind } from "../ports.ts";
import type { SettingsDeps } from "./SettingsSupport.ts";
import { ViewModel } from "./ViewModel.ts";
import type { WorkspaceViewModel } from "./WorkspaceViewModel.ts";

const POLL_MS = 2500;

export type DriveImportConnection = { id: string; label: string; adminEmail: string; createdAt: number };

export type DriveImportState = {
  status: "loading" | "ready" | "error";
  /** Owners and admins only (others see a note). */
  manager: boolean;
  /** The workspace's Google Workspace connection (mail import), if any. */
  connection?: DriveImportConnection;
  /** Bundle's own service account's client id, for the Admin console steps (absent with the workspace's own key). */
  googleClientId?: string;
  /** The latest Google Drive job (with its preview, units and problems once read). */
  job?: ImportJob;
  stage: DriveImportStage;
  /** Review: who and which shared drives come over. */
  choice: GoogleDriveImportOptions;
  /** Review: totals of the choice and whether they fit. */
  summary?: DriveReviewSummary;
  /** When "Check again" / "Read Google Drive" last asked Google. */
  checkedAt?: number;
  busy: boolean;
  notice?: MessageDescriptor;
  /** "Move to trash" / "Delete permanently" confirmation, with what it touches (counted by the server). */
  trash?: { jobId: string; action: "trash" | "delete"; counts: ImportTrashCounts; busy: boolean };
};

export class DriveImportViewModel extends ViewModel<DriveImportState> {
  private stopPoll: (() => void) | undefined;

  constructor(
    readonly ws: WorkspaceViewModel,
    private readonly deps: SettingsDeps,
  ) {
    super({ status: "loading", manager: false, stage: "connect", choice: { people: [], sharedDrives: [] }, busy: false });
    this.onDispose(ws.subscribe(() => this.follow()));
    this.onDispose(() => this.stopPoll?.());
    this.follow();
  }

  private follow(): void {
    const me = this.ws.state.cache?.members[this.ws.state.meId];
    const manager = me !== undefined && isManager(me, this.ws.deps.clock.now());
    if (manager !== this.state.manager) this.update({ manager });
  }

  /** All the scopes Bundle's Google import uses, Drive included (to paste over the delegation's list at once). */
  readonly allScopes: readonly string[] = GOOGLE_DRIVE_ALL_SCOPES;

  async load(): Promise<void> {
    try {
      const [sync, jobs] = await Promise.all([this.ws.deps.api.listMailSync(this.ws.wsId), this.deps.api.imports(this.ws.wsId)]);
      if (this.isDisposed) return;
      const g = sync.connections.find((c) => c.kind === "google" && c.google !== undefined);
      const connection = g ? { id: g.id, label: g.label, adminEmail: g.google!.adminEmail, createdAt: g.createdAt } : undefined;
      const latest = jobs.find((j) => j.source === "googleDrive");
      this.update({ status: "ready", connection, ...(sync.googleClientId ? { googleClientId: sync.googleClientId } : {}) });
      if (latest) await this.open(latest.id);
      else this.restage();
    } catch {
      if (!this.isDisposed && this.state.status !== "ready") this.update({ status: "error" });
    }
  }

  private async open(id: string): Promise<void> {
    try {
      const job = await this.deps.api.importJob(this.ws.wsId, id);
      if (!this.isDisposed) this.adopt(job);
    } catch (e) {
      if (!this.isDisposed) this.update({ notice: this.failure(e, "import.loadFailed") });
    }
  }

  private adopt(job: ImportJob): void {
    const prev = this.state.job;
    const patch: Partial<DriveImportState> = { job };
    // The review starts once from the server's proposal, or from the earlier import's choice ("Import again").
    if (job.status === "review" && job.googleDrive && (prev?.id !== job.id || prev.status !== "review")) {
      patch.choice = driveChoiceFrom(job.googleDrive, this.earlier);
    }
    this.update(patch);
    this.restage();
    if (isRunning(job.status) || job.undo?.status === "running") this.poll();
    else {
      this.stopPoll?.();
      this.stopPoll = undefined;
    }
  }

  private restage(): void {
    const job = this.state.job;
    const stage = driveImportStage(this.state.connection !== undefined, job);
    const summary = stage === "review" && job?.googleDrive ? driveReviewSummary(job.googleDrive, this.state.choice) : undefined;
    this.update({ stage, summary });
  }

  private poll(): void {
    this.stopPoll?.();
    this.stopPoll = this.ws.deps.clock.setTimer(POLL_MS, () => {
      this.stopPoll = undefined;
      const id = this.state.job?.id;
      if (id !== undefined && !this.isDisposed) void this.open(id);
    });
  }

  /** The choice the last started import made (prefills "Import again"). */
  private earlier: GoogleDriveImportOptions | undefined;

  // ---------- Reading ----------

  /** "Read Google Drive", "Check again" (scope), "Read again" (review) and "Import again" (ended): a new reading. */
  async read(): Promise<void> {
    if (this.state.busy || this.state.connection === undefined) return;
    const job = this.state.job;
    if (job?.googleDrive !== undefined && job.options?.googleDrive) this.earlier = job.options.googleDrive;
    else if (job?.status === "review") this.earlier = this.state.choice;
    this.update({ busy: true, notice: undefined, checkedAt: this.ws.deps.clock.now() });
    try {
      // A review waiting is dropped first (one Google Drive job at a time).
      if (job?.status === "review" || job?.status === "scanning") await this.deps.api.cancelImport(this.ws.wsId, job.id);
      const next = await this.deps.api.createImport(this.ws.wsId, { source: "googleDrive" });
      if (this.isDisposed) return;
      this.update({ busy: false });
      this.adopt(next);
    } catch (e) {
      if (!this.isDisposed) this.update({ busy: false, notice: this.failure(e, "import.startFailed") });
    }
  }

  // ---------- Review ----------

  togglePerson(email: string): void {
    const p = this.state.job?.googleDrive?.people.find((x) => x.email === email);
    if (p === undefined || p.memberId === undefined || p.error !== undefined) return;
    const people = this.state.choice.people.includes(email) ? this.state.choice.people.filter((e) => e !== email) : [...this.state.choice.people, email];
    this.update({ choice: { ...this.state.choice, people } });
    this.restage();
  }

  toggleSharedDrive(id: string): void {
    const d = this.state.job?.googleDrive?.sharedDrives.find((x) => x.id === id);
    if (d === undefined || d.readAs === undefined || d.error !== undefined) return;
    const sharedDrives = this.state.choice.sharedDrives.includes(id) ? this.state.choice.sharedDrives.filter((x) => x !== id) : [...this.state.choice.sharedDrives, id];
    this.update({ choice: { ...this.state.choice, sharedDrives } });
    this.restage();
  }

  /** Everyone / no one (people who can be picked). */
  setAllPeople(on: boolean): void {
    const preview = this.state.job?.googleDrive;
    if (!preview) return;
    const people = on ? preview.people.filter((p) => p.memberId !== undefined && p.error === undefined).map((p) => p.email) : [];
    this.update({ choice: { ...this.state.choice, people } });
    this.restage();
  }

  /** Start is on: something chosen and it fits in Files storage. */
  canStart(): boolean {
    const s = this.state.summary;
    return this.state.stage === "review" && !this.state.busy && s !== undefined && !s.empty && !s.over;
  }

  async start(): Promise<void> {
    const job = this.state.job;
    if (job === undefined || !this.canStart()) return;
    this.update({ busy: true, notice: undefined });
    try {
      const next = await this.deps.api.startImport(this.ws.wsId, job.id, { googleDrive: this.state.choice });
      if (this.isDisposed) return;
      this.update({ busy: false });
      this.adopt(next);
    } catch (e) {
      if (!this.isDisposed) this.update({ busy: false, notice: this.failure(e, "import.startFailed") });
    }
  }

  /** "Cancel" while reading, "Discard" in review, "Cancel import" while importing (what's copied stays). */
  async cancel(): Promise<void> {
    const job = this.state.job;
    if (job === undefined || this.state.busy || !(isRunning(job.status) || job.status === "review")) return;
    this.update({ busy: true });
    try {
      const next = await this.deps.api.cancelImport(this.ws.wsId, job.id);
      if (this.isDisposed) return;
      this.update({ busy: false });
      this.adopt(next);
    } catch (e) {
      if (!this.isDisposed) this.update({ busy: false, notice: this.failure(e, "import.cancelFailed") });
    }
  }

  // ---------- Ended ----------

  /** What didn't come over, as a CSV file's text ("Download CSV"). */
  problemsCsv(): string {
    return driveProblemsCsv(this.state.job?.driveProblems ?? []);
  }

  /** The ended import can be moved to the trash / deleted (no undo running). */
  canUndo(): boolean {
    const job = this.state.job;
    return this.state.stage === "ended" && job !== undefined && job.undo?.status !== "running";
  }

  askTrash(): Promise<void> {
    return this.ask("trash");
  }

  askDelete(): Promise<void> {
    return this.ask("delete");
  }

  private async ask(action: "trash" | "delete"): Promise<void> {
    const job = this.state.job;
    if (job === undefined || !this.canUndo() || this.state.busy) return;
    this.update({ busy: true, notice: undefined });
    try {
      const counts = action === "trash" ? await this.deps.api.importTrash(this.ws.wsId, job.id) : await this.deps.api.importDeletable(this.ws.wsId, job.id);
      if (this.isDisposed) return;
      if ((counts.folders ?? 0) + counts.files === 0) this.update({ busy: false, notice: msg(action === "trash" ? "import.trash.nothing" : "import.delete.nothing") });
      else this.update({ busy: false, trash: { jobId: job.id, action, counts, busy: false } });
    } catch (e) {
      if (!this.isDisposed) this.update({ busy: false, notice: this.failure(e, action === "trash" ? "import.trash.failed" : "import.delete.failed") });
    }
  }

  dismissTrash(): void {
    if (this.state.trash && !this.state.trash.busy) this.update({ trash: undefined });
  }

  async confirmTrash(): Promise<void> {
    const t = this.state.trash;
    if (t === undefined || t.busy) return;
    this.update({ trash: { ...t, busy: true } });
    try {
      const undo = t.action === "trash" ? await this.deps.api.trashImport(this.ws.wsId, t.jobId) : await this.deps.api.deleteImport(this.ws.wsId, t.jobId);
      if (this.isDisposed) return;
      this.update({ trash: undefined });
      const job = this.state.job;
      if (job?.id === t.jobId) this.adopt({ ...job, undo });
    } catch (e) {
      if (!this.isDisposed) this.update({ trash: undefined, notice: this.failure(e, t.action === "trash" ? "import.trash.failed" : "import.delete.failed") });
    }
  }

  dismissNotice(): void {
    this.update({ notice: undefined });
  }

  private failure(e: unknown, fallback: MessageKey): MessageDescriptor {
    if (errorKind(e) === "network") return msg("settings.offline");
    if (e instanceof ApiFailure && e.code === "busy") return msg("import.busy");
    if (e instanceof ApiFailure && e.code === "no_google") return msg("import.drive.noGoogle");
    return msg(fallback);
  }
}
